import { useBuildVars } from 'src/config';
import {
  Applicability,
  CustomerGroupList,
  ProductGroupList,
} from './ingramProgramsSearch';
import { useApiDownload, useApiPatch, useApiPost } from 'src/api';
import { useMutation, useQueryClient } from 'react-query';
import { useAccount } from '@azure/msal-react';
import { useSearchParams } from 'react-router-dom';

export interface ManualTemplatesFieldProperties {
  vendorACOPDiscountType?: string;
}

export interface CreateRequestPreamble {
  createdBy?: string;
  createdDate?: string;
  customerGroupInfo?: CustomerGroupList[];
  ingramProgramName?: string;
  ingramVendorProgramId?: number;
  lastChangeUser?: string;
  lastChangedDate?: string;
  productGroupInfo?: ProductGroupList[];
  programsDescription?: string;
}

export interface CreateProgramRequest {
  manualTemplatesFieldProperties?: ManualTemplatesFieldProperties;
  requestPreamble?: CreateRequestPreamble;
}

export function useCreateIngramProgram(body: CreateProgramRequest) {
  const buildVars = useBuildVars();
  const userName = useAccount()?.username ?? '';
  const queryClient = useQueryClient();
  const apiPost = useApiPost({ host: buildVars.VP_API_BASE });
  return useMutation(
    () =>
      apiPost(
        '/system.webapi.gds/api/v1/GdsCoreApiIngramProgram/CreateIngramProgram',
        {
          ...body,
          requestPreamble: { ...body.requestPreamble, createdBy: userName },
        }
      ),
    {
      onError: () =>
        queryClient.invalidateQueries(
          '/system.webapi.gds/api/v1/GdsCoreApiIngramProgram/GetIngramVendorPrograms'
        ),
      onSuccess: () =>
        queryClient.invalidateQueries(
          '/system.webapi.gds/api/v1/GdsCoreApiIngramProgram/GetIngramVendorPrograms'
        ),
    }
  );
}

export function useUpdateIngramProgram(body: CreateProgramRequest) {
  const buildVars = useBuildVars();
  const queryClient = useQueryClient();
  const userName = useAccount()?.username ?? '';
  const apiPatch = useApiPatch(buildVars.VP_API_BASE);
  return useMutation(
    () =>
      apiPatch(
        '/system.webapi.gds/api/v1/GdsCoreApiIngramProgram/UpdateIngramProgram',
        {
          ...body,
          requestPreamble: {
            ...body.requestPreamble,
            lastChangeUser: userName,
            lastChangedDate: new Date().toISOString(),
          },
        }
      ),
    {
      onError: () =>
        queryClient.invalidateQueries(
          '/system.webapi.gds/api/v1/GdsCoreApiIngramProgram/EditIngramProgram'
        ),
      onSuccess: () =>
        queryClient.invalidateQueries(
          '/system.webapi.gds/api/v1/GdsCoreApiIngramProgram/EditIngramProgram'
        ),
    }
  );
}

export interface CreateIngramDiscountRequest {
  applicability?: Applicability[];
  company_code?: string;
  country_code?: string;
  discount_amount?: string;
  discount_calc_type?: string;
  discount_type?: string;
  effective_date?: string;
  expiration_date?: string;
  name?: string;
  program_id?: number;
  status_code?: string;
}

export function useCreateIngramDiscount(body: CreateIngramDiscountRequest) {
  const buildVars = useBuildVars();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const companyCode = searchParams.get('companyCode');
  const countryCode = searchParams.get('countryCode');
  const apiPost = useApiPost({ host: buildVars.VP_API_BASE });
  return useMutation(
    () =>
      apiPost(
        '/system.webapi.gds/api/v1/GdsCoreApiIngramDiscounts/CreateIngramDiscount',
        { ...body, company_code: companyCode, country_code: countryCode }
      ),
    {
      onError: () =>
        queryClient.invalidateQueries(
          '/system.webapi.gds/api/v1/GdsCoreApiIngramDiscounts/GetIngramDiscount'
        ),
      onSuccess: () =>
        queryClient.invalidateQueries(
          '/system.webapi.gds/api/v1/GdsCoreApiIngramDiscounts/GetIngramDiscount'
        ),
    }
  );
}

export function useUpdateIngramDiscount(body: CreateIngramDiscountRequest) {
  const buildVars = useBuildVars();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const companyCode = searchParams.get('companyCode');
  const countryCode = searchParams.get('countryCode');
  const apiPatch = useApiPatch(buildVars.VP_API_BASE);
  return useMutation(
    () =>
      apiPatch(
        '/system.webapi.gds/api/v1/GdsCoreApiIngramDiscounts/UpdateIngramDiscount',
        { ...body, company_code: companyCode, country_code: countryCode }
      ),
    {
      onError: () =>
        queryClient.invalidateQueries(
          '/system.webapi.gds/api/v1/GdsCoreApiIngramDiscounts/GetIngramDiscount'
        ),
      onSuccess: () =>
        queryClient.invalidateQueries(
          '/system.webapi.gds/api/v1/GdsCoreApiIngramDiscounts/GetIngramDiscount'
        ),
    }
  );
}

export function useGetExcelReport(isProgramOrDiscount: boolean, params: any) {
  const buildVars = useBuildVars();
  const apiDownload = useApiDownload({ host: buildVars.VP_API_BASE });
  return useMutation(() =>
    apiDownload(
      isProgramOrDiscount
        ? '/system.webapi.gds/api/v1/GdsCoreApiIngramProgram/IngramVendorPrograms.xlsx'
        : '/system.webapi.gds/api/v1/GdsCoreApiIngramDiscounts/IngramDiscount.xlsx',
      params
    )
  );
}
